import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar CAPTCHA
    if (!captchaValue) {
      toast.error('Por favor completa el CAPTCHA');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { 
        email,
        captcha_token: captchaValue  // Enviar token de CAPTCHA
      });
      
      setSuccess(true);
      setResetLink(response.data.reset_link); // Solo para testing
      toast.success('Instrucciones enviadas. Revisa tu email.');
    } catch (error) {
      console.error('Error requesting password reset:', error);
      toast.error('Error al procesar la solicitud');
      // Reset CAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaValue(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f5f5ff 100%)' }}>
        <Card className="max-w-md w-full mx-4" data-testid="success-message">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <CardTitle className="gotham-bold text-2xl" style={{ color: '#272b96' }}>
              ¡Revisa tu Email!
            </CardTitle>
            <CardDescription className="verdana-regular text-gray-600">
              Si el email existe en nuestro sistema, recibirás instrucciones para restablecer tu contraseña.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {resetLink && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="verdana-regular text-sm text-gray-700 mb-2">
                  <strong>Modo Testing:</strong> Link de restablecimiento:
                </p>
                <a
                  href={resetLink}
                  className="verdana-regular text-xs text-purple-600 break-all hover:underline"
                  data-testid="reset-link"
                >
                  {resetLink}
                </a>
              </div>
            )}
            
            <Button
              onClick={() => navigate('/')}
              className="w-full btn-viubux"
              data-testid="back-to-login-btn"
            >
              Volver al Inicio de Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f5f5ff 100%)' }}>
      <Card className="max-w-md w-full mx-4" data-testid="forgot-password-card">
        <CardHeader>
          <CardTitle className="gotham-bold text-3xl text-center" style={{ color: '#272b96' }}>
            ¿Olvidaste tu Contraseña?
          </CardTitle>
          <CardDescription className="verdana-regular text-center text-gray-600">
            Ingresa tu email y te enviaremos instrucciones para restablecerla
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="verdana-regular text-gray-700 text-sm mb-2 block">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-lg border verdana-regular focus:outline-none focus:ring-2"
                  style={{ borderColor: '#e5e7eb', '--tw-ring-color': '#7012b2' }}
                  required
                />
              </div>
            </div>

            {/* CAPTCHA */}
            {RECAPTCHA_SITE_KEY && (
              <div className="flex justify-center" data-testid="recaptcha-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                />
              </div>
            )}

            <Button
              data-testid="submit-reset-btn"
              type="submit"
              className="w-full btn-viubux"
              disabled={loading}
            >
              {loading ? 'Enviando...' : 'Enviar Instrucciones'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="verdana-regular text-sm flex items-center gap-2 mx-auto hover:opacity-80"
              style={{ color: '#7012b2' }}
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Inicio de Sesión
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
